import React, { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useDenaliContext } from '../../../DenaliProvider'
import { useCheckMigrationsMutation, useScanMutation, useSyncMutation } from '../../../../../../api/integrations'
import { SUCCESS_ALERT } from '../../../../../../constants'

export const useConnectionActions = (root) => {
  const denaliContext = useDenaliContext()
  const { mutateAsync: syncJob } = useSyncMutation()
  const { mutateAsync: scanJob } = useScanMutation()
  const { mutateAsync: migrate } = useCheckMigrationsMutation()

  const handleSync = useCallback(async (connection) => {
    const result = await syncJob({
      connectionId: connection.connectionId,
      domain: connection.domain
    })

    denaliContext.setAlert({
      ...SUCCESS_ALERT,
      alertMessage: (
        <Link to={`${root}/integrator/batches/${result.batchId}`}>Sync started in batch {result.batchId}</Link>
      )
    })
    return result
  }, [syncJob, denaliContext, root])

  const handleScan = useCallback(async (connection) => {
    const result = await scanJob({
      connectionId: connection.connectionId,
      domain: connection.domain
    })

    denaliContext.setAlert({
      ...SUCCESS_ALERT,
      alertMessage: (
        <Link to={`${root}/integrator/batches/${result.batchId}`}>Scan started in batch {result.batchId}</Link>
      )
    })
    return result
  }, [scanJob, denaliContext, root])

  const handleMigrate = useCallback(async (connection) => {
    const result = await migrate({
      connection: connection.connectionId,
      domain: connection.domain
    })

    denaliContext.setAlert({
      ...SUCCESS_ALERT,
      alertMessage: (
        <Link to={`${root}/integrator/batches/${result.batchId}`}>Migration Check started in batch {result.batchId}</Link>
      )
    })
    return result
  }, [migrate, denaliContext, root])

  return useMemo(() => {
    return [
      { key: 'handle-connection-scan', label: 'Scan', icon: 'search', onClick: handleScan },
      { key: 'handle-connection-sync', label: 'Sync', icon: 'sync', onClick: handleSync },
      { key: 'handle-connection-migrate', label: 'Check Migrations', icon: 'database', onClick: handleMigrate }
    ]
  }, [handleSync, handleScan, handleMigrate])
}

export const useGroupedConnections = (data, searchText) => {
  return useMemo(() => {
    const conns = data?.connections || []
    const needle = (searchText || '').trim().toLowerCase()
    const filtered = needle
      ? conns.filter(c =>
        c.displayName?.toLowerCase().includes(needle) ||
        c.code?.toLowerCase().includes(needle) ||
        c.domain?.toLowerCase().includes(needle)
      )
      : conns
    const groups = {}
    for (const conn of filtered) {
      const domain = conn.domain || 'unknown'
      if (!groups[domain]) {
        groups[domain] = { domainName: conn.domainName || domain, connections: [] }
      }
      groups[domain].connections.push(conn)
    }
    return Object.entries(groups)
      .sort(([, a], [, b]) => a.domainName.localeCompare(b.domainName))
      .map(([domain, { domainName, connections }]) => ({ domain, domainName, connections }))
  }, [data, searchText])
}
